import React, { useCallback, useEffect, useRef, useState } from "react";
import { HelpButton } from "@/components/HelpAccess";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Microphone, SpeakerHigh, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { extForMime } from "@/lib/useRecorder";
import { LEAD_IN_VARIANTS, exchangeStage } from "@/lib/followUps";
import { liveStrings } from "@/lib/liveInterviewI18n";
import {
  createTurnTracker,
  rmsFromAnalyser,
  START_COUNTDOWN_S,
  STILL_LISTENING_HINT_MS,
} from "@/lib/turnTaking";

// The Live Interview (LIVE_INTERVIEW_PLAN.md §8, decisions L10-L15, 2026-10-30).
// Audio only, one screen for the whole interview, hands-free:
//   question audio → the microphone opens and a countdown to START answering runs
//   (30 s, 20 s for a follow-up) → speaking stops the countdown, with no limit on the
//   answer's length → a few seconds of silence (lib/turnTaking.js) or "I'm finished"
//   ends the answer → the same answer, follow-up and scoring endpoints as Guided.
// If the countdown reaches 0 with no speech: "No answer given" and the next question.
// "Could you repeat the question?" (2026-10-30): a short first phrase and a pause are
// checked at once (/repeat-check); if it was a repeat request, listening stops, the
// interviewer repeats the question and the countdown starts again from the beginning.
// Guided (Interview.jsx) is untouched; this screen is chosen by session.mode === "live".

const SAMPLE_MS = 100;
const TTS_TIMEOUT_MS = 45000;
const CLIP_FETCH_MS = 3000; // a transition that takes longer to fetch is skipped
const CLIP_MAX_MS = 6000;
const QUESTION_MAX_MS = 90000; // never wait forever for a question clip to report its end
const CUTOFF_WATCH_MS = 2000; // after an automatic end, did they keep talking?
const TRANSITION_VARIANTS = 3;
const SEND_ATTEMPTS = 3;

function blobUrlFromBase64(data) {
  const bin = atob(data.audio_base64);
  const bytes = new Uint8Array(bin.length);
  for (let k = 0; k < bin.length; k++) bytes[k] = bin.charCodeAt(k);
  return URL.createObjectURL(new Blob([bytes], { type: data.mime || "audio/mpeg" }));
}

function pickMime() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  return ["audio/webm;codecs=opus", "audio/webm", "audio/mp4;codecs=mp4a.40.2", "audio/mp4", "audio/aac"]
    .find((m) => MediaRecorder.isTypeSupported(m)) || "";
}

export default function LiveInterview({ session, onUseGuided }) {
  const navigate = useNavigate();
  const sessionId = session.session_id;
  const s = liveStrings(session.interface_language || "en");
  const total = session.questions?.length || 0;

  const [phase, setPhase] = useState("start"); // start | speaking | waiting | answering | sending | completing | micfail
  const [qIdx, setQIdx] = useState(0);
  const [onClosing, setOnClosing] = useState(false); // S5: "Do you have any questions for me?"
  const [onFollowUp, setOnFollowUp] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [stillListening, setStillListening] = useState(false);

  const audioElRef = useRef(null);
  const streamRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const bufferRef = useRef(null);
  const finishRef = useRef(null); // ends the current answer ("I'm finished")
  const cancelledRef = useRef(false);
  const clipCacheRef = useRef({});
  // Wait times (2026-11-04): from sending an answer until the interviewer speaks again.
  const waitRef = useRef(null);

  // ── Cleanup ──────────────────────────────────────────────────────────────
  const releaseMic = useCallback(() => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (ctxRef.current) ctxRef.current.close().catch(() => {});
    ctxRef.current = null;
    analyserRef.current = null;
  }, []);

  useEffect(() => () => {
    cancelledRef.current = true;
    if (audioElRef.current) audioElRef.current.pause();
    releaseMic();
  }, [releaseMic]);

  const logEvent = (event) => {
    api.post(`/sessions/${sessionId}/live-events`, event).catch(() => {}); // tuning data only
  };

  // ── Audio out ────────────────────────────────────────────────────────────
  const playUrl = (url, maxMs) =>
    new Promise((resolve) => {
      const el = audioElRef.current;
      if (!el || cancelledRef.current) return resolve();
      const timer = setTimeout(done, maxMs);
      function done() {
        clearTimeout(timer);
        el.removeEventListener("ended", done);
        el.removeEventListener("error", done);
        resolve();
      }
      el.addEventListener("ended", done);
      el.addEventListener("error", done);
      el.pause();
      el.src = url;
      el.currentTime = 0;
      el.play().catch(done);
      const wait = waitRef.current;
      if (wait) {
        waitRef.current = null;
        logEvent({ ...wait.event, kind: "wait", wait_ms: Math.round(performance.now() - wait.sentAt) });
      }
    });

  const fetchClip = (key, request) => {
    if (!clipCacheRef.current[key]) {
      clipCacheRef.current[key] = request()
        .then(({ data }) => blobUrlFromBase64(data))
        .catch((e) => {
          delete clipCacheRef.current[key];
          throw e;
        });
    }
    return clipCacheRef.current[key];
  };

  const questionClip = (i, followUpId = "") =>
    fetchClip(followUpId ? `q${i}:${followUpId}` : `q${i}`, () => {
      const fd = new FormData();
      fd.append("question_index", String(i));
      fd.append("variant", "original");
      if (followUpId) fd.append("follow_up_id", followUpId);
      return api.post(`/sessions/${sessionId}/tts`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: TTS_TIMEOUT_MS,
      });
    });

  const transitionClip = (kind, variant = 0) =>
    fetchClip(`t:${kind}:${variant}`, () => {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("variant", String(variant));
      return api.post(`/sessions/${sessionId}/tts-transition`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: TTS_TIMEOUT_MS,
      });
    });

  const playTransition = async (kind, variant = 0) => {
    try {
      const url = await Promise.race([
        transitionClip(kind, variant),
        new Promise((_, reject) => setTimeout(() => reject(new Error("slow")), CLIP_FETCH_MS)),
      ]);
      await playUrl(url, CLIP_MAX_MS);
    } catch {
      /* a missing transition never holds up the interview */
    }
  };

  const playQuestion = async (i, followUpId = "") => {
    setPhase("speaking");
    try {
      await playUrl(await questionClip(i, followUpId), QUESTION_MAX_MS);
    } catch {
      toast.error(s.sendFail);
    }
  };

  // ── Listening: countdown to start, then until silence or "I'm finished" ───
  // Resolves {kind: "no_answer", countdownLeft}, {kind: "repeat_request"} or
  // {kind: "answer", blob, endedBy, answerMs}.
  const listen = (isFollowUp) =>
    new Promise((resolve) => {
      const stream = streamRef.current;
      const analyser = analyserRef.current;
      const mime = pickMime();
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      recorder.ondataavailable = (e) => e.data && e.data.size && chunks.push(e.data);
      const tracker = createTurnTracker();
      const limitS = isFollowUp ? START_COUNTDOWN_S.follow_up : START_COUNTDOWN_S.main;
      const openedAt = performance.now();
      let speechAt = null;
      let settled = false;
      let repeatChecked = false;

      setPhase("waiting");
      setCountdown(limitS);
      setStillListening(false);
      recorder.start();

      const stopRecorder = () =>
        new Promise((done) => {
          if (recorder.state === "inactive") return done();
          recorder.onstop = done;
          recorder.stop();
        });

      const settle = async (result) => {
        if (settled) return;
        settled = true;
        clearInterval(timer);
        finishRef.current = null;
        await stopRecorder();
        setCountdown(null);
        setStillListening(false);
        if (result.kind === "answer") result.blob = new Blob(chunks, { type: recorder.mimeType || mime || "audio/webm" });
        resolve(result);
      };

      const timer = setInterval(() => {
        if (cancelledRef.current) return settle({ kind: "cancelled" });
        const now = performance.now();
        const event = tracker.sample(now - openedAt, rmsFromAnalyser(analyser, bufferRef.current));
        if (!tracker.speaking) {
          const left = Math.max(0, limitS - Math.floor((now - openedAt) / 1000));
          setCountdown(left);
          if (now - openedAt >= limitS * 1000) settle({ kind: "no_answer", countdownLeft: 0 });
          return;
        }
        if (event === "speech_start") {
          speechAt = now;
          setPhase("answering");
          setCountdown(null);
        }
        if (speechAt && now - speechAt >= STILL_LISTENING_HINT_MS) setStillListening(true);
        if (!repeatChecked && tracker.shortUtterancePaused(now - openedAt)) {
          repeatChecked = true; // once per question: a longer reply is an answer
          checkRepeat(recorder, chunks, mime).then((repeat) => {
            if (repeat) settle({ kind: "repeat_request" });
          });
        }
        if (event === "end_of_answer") {
          settle({ kind: "answer", endedBy: "silence", answerMs: Math.round(now - speechAt),
            silenceLimitMs: tracker.silenceLimitMs, tracker });
        }
      }, SAMPLE_MS);

      finishRef.current = () => {
        if (!tracker.speaking) return; // nothing said yet: the countdown decides
        settle({ kind: "answer", endedBy: "button", answerMs: Math.round(performance.now() - speechAt),
          silenceLimitMs: tracker.silenceLimitMs });
      };
    });

  // Is what they've said so far a request to repeat the question? (The recording keeps
  // running meanwhile, so nothing is lost if it wasn't.)
  const checkRepeat = (recorder, chunks, mime) =>
    new Promise((resolve) => {
      const send = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || mime || "audio/webm" });
        const fd = new FormData();
        fd.append("audio", blob, `check.${extForMime(blob.type)}`);
        api.post(`/sessions/${sessionId}/repeat-check`, fd, { headers: { "Content-Type": "multipart/form-data" } })
          .then(({ data }) => resolve(Boolean(data?.repeat)))
          .catch(() => resolve(false));
      };
      if (recorder.state !== "recording") return resolve(false);
      const flushed = () => {
        recorder.removeEventListener("dataavailable", flushed);
        send();
      };
      recorder.addEventListener("dataavailable", flushed);
      recorder.requestData(); // the audio so far
    });

  // After an automatic end: did the candidate keep talking (a cut-off)? Logged for tuning.
  const watchForCutOff = (base) => {
    const tracker = base.tracker;
    const analyser = analyserRef.current;
    if (!tracker || !analyser) return;
    const started = performance.now();
    let resumed = false;
    const timer = setInterval(() => {
      if (tracker.isSpeech(rmsFromAnalyser(analyser, bufferRef.current))) resumed = true;
      if (performance.now() - started >= CUTOFF_WATCH_MS) {
        clearInterval(timer);
        logEvent({ kind: "answer_end", question_index: base.questionIndex, follow_up: base.followUp,
          ended_by: "silence", answer_ms: base.answerMs, silence_limit_ms: base.silenceLimitMs, speech_resumed: resumed });
      }
    }, SAMPLE_MS);
  };

  const send = async (path, form) => {
    for (let attempt = 1; ; attempt++) {
      try {
        const { data } = await api.post(`/sessions/${sessionId}/${path}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return data;
      } catch (e) {
        if (attempt >= SEND_ATTEMPTS || cancelledRef.current) throw e;
        toast.error(s.sendFail);
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
    }
  };

  // One listen-and-send turn. Returns the backend's answer ({action, follow_up}) or
  // {action: "none", no_answer: true}; replays the clip on "repeat the question".
  const answerTurn = async (i, isFollowUp, replay) => {
    for (;;) {
      const heard = await listen(isFollowUp);
      if (heard.kind === "cancelled") return null;
      if (heard.kind === "repeat_request") {
        logEvent({ kind: "repeat", question_index: i, follow_up: isFollowUp });
        await replay(); // then listen() starts a fresh countdown
        continue;
      }
      setPhase("sending");
      if (heard.kind === "no_answer") {
        logEvent({ kind: "no_answer", question_index: i, follow_up: isFollowUp, countdown_left_s: 0 });
        const fd = new FormData();
        fd.append("question_index", String(i));
        fd.append("follow_up", String(isFollowUp));
        return await send("no-answer", fd);
      }
      if (heard.endedBy === "silence") {
        watchForCutOff({ ...heard, questionIndex: i, followUp: isFollowUp });
      } else {
        logEvent({ kind: "answer_end", question_index: i, follow_up: isFollowUp, ended_by: heard.endedBy,
          answer_ms: heard.answerMs, silence_limit_ms: heard.silenceLimitMs });
      }
      const fd = new FormData();
      fd.append("audio", heard.blob, `answer.${extForMime(heard.blob.type)}`);
      fd.append("question_index", String(i));
      const sentAt = performance.now();
      const data = await send(isFollowUp ? "follow-up-answer" : "answer", fd);
      waitRef.current = { sentAt, event: {
        question_index: i, follow_up: isFollowUp, send_ms: Math.round(performance.now() - sentAt),
        ...(data?.timing || {}),
      } };
      if (data.action === "repeat_question") {
        logEvent({ kind: "repeat", question_index: i, follow_up: isFollowUp });
        await replay();
        continue;
      }
      if (data.action === "repeat") {
        await playTransition("repeat"); // "I didn't understand your response. Could you repeat it?"
        continue;
      }
      return data;
    }
  };

  const runQuestion = async (i, resumeFollowUp) => {
    setQIdx(i);
    setOnFollowUp(false);
    let result = null;
    if (!resumeFollowUp) {
      await playQuestion(i);
      result = await answerTurn(i, false, () => playQuestion(i));
      if (!result) return false;
    } else {
      result = { action: "follow_up", follow_up: resumeFollowUp };
    }
    if (result.action === "follow_up" && result.follow_up?.id) {
      setOnFollowUp(true);
      if (!resumeFollowUp) await playTransition("lead_in", i % LEAD_IN_VARIANTS);
      await playQuestion(i, result.follow_up.id);
      const followUp = await answerTurn(i, true, () => playQuestion(i, result.follow_up.id));
      if (!followUp) return false;
    }
    const isLast = i >= total - 1;
    setPhase("speaking");
    // S5: after the last question, the closing question comes before the end.
    await playTransition(isLast ? (closingDue ? "closing_question" : "end") : "next", i % TRANSITION_VARIANTS);
    return true;
  };

  // S5 (2026-11-01): the closing question. The interviewer never answers the candidate's
  // questions: a neutral reply, then the usual end. No follow-up; a repeat request
  // replays the question; a failed send still lets the interview finish.
  const closingDue = !session?.free_trial && !session?.closing;
  const runClosing = async (askFirst) => {
    setOnClosing(true);
    setOnFollowUp(false);
    if (askFirst) {
      setPhase("speaking");
      await playTransition("closing_question");
    }
    for (;;) {
      const heard = await listen(false);
      if (heard.kind === "cancelled") return false;
      if (heard.kind === "repeat_request") {
        setPhase("speaking");
        await playTransition("closing_question");
        continue;
      }
      setPhase("sending");
      const fd = new FormData();
      if (heard.kind === "no_answer") fd.append("no_answer", "true");
      else fd.append("audio", heard.blob, `closing.${extForMime(heard.blob.type)}`);
      let data = { has_questions: false };
      try {
        ({ data } = await api.post(`/sessions/${sessionId}/closing-answer`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        }));
      } catch {
        /* the interview still finishes; the report just has no closing section */
      }
      if (data.action === "repeat_question") {
        setPhase("speaking");
        await playTransition("closing_question");
        continue;
      }
      setPhase("speaking");
      await playTransition(data.has_questions ? "closing_reply" : "closing_reply_none");
      await playTransition("end_after_closing");
      return true;
    }
  };

  const run = async () => {
    const answers = session.answers || [];
    const stageOf = (i) => exchangeStage(answers.find((a) => a.question_index === i));
    let first = 0;
    while (first < total && stageOf(first) === "done") first++;
    if (first === 0 && !answers.length) {
      setPhase("speaking");
      await playTransition("welcome");
    }
    for (let i = first; i < total; i++) {
      if (cancelledRef.current) return;
      const answer = answers.find((a) => a.question_index === i);
      const resume = i === first && stageOf(i) === "follow_up" ? answer.follow_up : null;
      // A question whose answer must be repeated starts again from its question audio.
      const ok = await runQuestion(i, resume);
      if (!ok) return;
    }
    // S5: asked after Question 10 (its question was just played, unless the
    // interview resumed with every question already answered).
    if (closingDue && !cancelledRef.current) {
      const ok = await runClosing(first >= total);
      if (!ok) return;
    }
    releaseMic();
    setPhase("completing");
    try {
      await api.post(`/sessions/${sessionId}/complete`);
      navigate(`/report/${sessionId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || s.reportFail);
    }
  };

  // ── Start: the microphone (a user gesture, so audio and recording are allowed) ──
  const start = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") throw new Error("unsupported");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      await ctx.resume();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      ctx.createMediaStreamSource(stream).connect(analyser);
      streamRef.current = stream;
      ctxRef.current = ctx;
      analyserRef.current = analyser;
      bufferRef.current = new Float32Array(analyser.fftSize);
    } catch {
      releaseMic();
      setPhase("micfail"); // L6: never switch silently
      return;
    }
    // Fetch the first question while the welcome plays.
    questionClip(0).catch(() => {});
    run();
  };

  const chooseGuided = () => {
    logEvent({ kind: "fallback_to_guided" });
    onUseGuided();
  };

  // ── Screen ───────────────────────────────────────────────────────────────
  const status = {
    speaking: s.speaking,
    waiting: countdown !== null ? s.startIn(countdown) : s.listening,
    answering: stillListening ? s.stillListening : s.listening,
    sending: s.sending,
    completing: s.completing,
  }[phase];
  const listeningNow = phase === "waiting" || phase === "answering";

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)]" data-testid="live-interview">
      <audio ref={audioElRef} preload="auto" className="hidden" />
      {/* The same top bar as Guided Mode (product owner, 2026-11-02): brand, Restart and
          End Interview (InterviewLifecycleControls fills live-lifecycle-host) and the
          account menu, which also leads admins to the Admin panel (SessionAllowanceTracker
          fills live-account-host). */}
      <header className="border-b border-[color:var(--border-stone)] bg-white" data-testid="live-header">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-10 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex min-w-0 shrink-0 items-center gap-3 mr-auto">
            <img src="/brand/globe-logo.svg" alt="English Job Interview Simulator"
              className="h-[48px] w-[66px] sm:h-[56px] sm:w-[76px] shrink-0 object-contain" />
            <div className="leading-tight">
              <div className="hidden sm:block whitespace-nowrap text-[10px] uppercase tracking-[0.25em] text-[color:var(--brand)]">English Job Interview Simulator</div>
              <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">{s.eyebrow}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {phase !== "start" && phase !== "micfail" && (
              <div className="text-sm text-[color:var(--text-secondary)]" data-testid="live-progress">
                {onClosing ? s.closing : s.questionOf(Math.min(qIdx + 1, total), total)}
                {onFollowUp && !onClosing ? ` · ${s.followUp}` : ""}
              </div>
            )}
            {/* Help only between questions (HELP_SYSTEM_PLAN.md D5): never while the
                interviewer speaks, the countdown runs or the candidate answers. */}
            <HelpButton className="h-10 px-4 py-0 text-sm" disabled={["speaking", "waiting", "answering"].includes(phase)} />
            <div className="flex items-center gap-3" data-testid="live-lifecycle-host" />
            <div className="shrink-0" data-testid="live-account-host" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 md:p-10">
        {phase === "start" && (
          <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-6 space-y-4" data-testid="live-start">
            <h1 className="text-2xl font-semibold text-[color:var(--text-primary)]">{s.startTitle}</h1>
            <ul className="space-y-2 text-sm text-[color:var(--text-secondary)] leading-relaxed list-disc pl-5">
              {s.startBody.map((line) => <li key={line}>{line}</li>)}
            </ul>
            <p className="text-xs text-[color:var(--text-secondary)]">{s.micNeeded}</p>
            <button type="button" onClick={start} data-testid="live-start-button"
              className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-6 py-3 text-sm font-medium">
              {s.startButton}
            </button>
          </section>
        )}

        {phase === "micfail" && (
          <section className="rounded-2xl border border-[color:var(--score-bad)]/40 bg-white p-6 space-y-4" role="alert" data-testid="live-micfail">
            <h1 className="text-xl font-semibold text-[color:var(--text-primary)]">{s.micFailTitle}</h1>
            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">{s.micFailBody}</p>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={chooseGuided} data-testid="live-use-guided"
                className="rounded-full bg-[color:var(--brand)] text-white px-5 py-2.5 text-sm font-medium">{s.micFailGuided}</button>
              <button type="button" onClick={() => setPhase("start")} data-testid="live-mic-retry"
                className="rounded-full border border-[color:var(--border-stone)] px-5 py-2.5 text-sm">{s.micFailRetry}</button>
            </div>
          </section>
        )}

        {status && (
          <section className="flex flex-col items-center text-center pt-10 space-y-8" data-testid="live-stage">
            <div className={`h-40 w-40 rounded-full flex items-center justify-center border-4 transition-colors ${
              listeningNow ? "border-[color:var(--brand)] bg-[color:var(--brand)]/10" : "border-[color:var(--border-stone)] bg-white"}`}>
              {phase === "speaking" ? <SpeakerHigh size={56} className="text-[color:var(--text-secondary)]" />
                : phase === "completing" ? <CheckCircle size={56} className="text-[color:var(--brand)]" />
                  : <Microphone size={56} className={listeningNow ? "text-[color:var(--brand)]" : "text-[color:var(--text-secondary)]"} />}
            </div>
            <div className="text-lg font-medium text-[color:var(--text-primary)]" aria-live="polite" data-testid="live-status">{status}</div>
            {phase === "answering" && (
              <button type="button" onClick={() => finishRef.current && finishRef.current()} data-testid="live-finished"
                className="rounded-full border border-[color:var(--border-stone)] bg-white px-6 py-3 text-sm font-medium hover:border-[color:var(--brand)]">
                {s.finished}
              </button>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
